import { X, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/app/context/AuthContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '@/app/components/Logo';
import { useMaintenanceMode } from '@/app/contexts/MaintenanceModeContext';
import { API_ENDPOINTS } from '@/config/api';
import { trackSignupStart, trackSignupComplete, trackSignupError, identifyUser } from '@/lib/analytics';
import { sendLicenseToExtension, isExtensionSource } from '@/lib/extensionBridge';

// Google Client ID - used by the global Google Identity Services initialized in App.tsx
const GOOGLE_CLIENT_ID = '71406201363-hohot5f8q570o8sehmrf1l86vgqc0sui.apps.googleusercontent.com';

// LinkedIn OAuth configuration
const LINKEDIN_CLIENT_ID = import.meta.env.VITE_LINKEDIN_CLIENT_ID || '';
const LINKEDIN_REDIRECT_URI = import.meta.env.VITE_LINKEDIN_REDIRECT_URI || `${window.location.origin}/auth/linkedin/callback`;

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const { login } = useAuth();
  const { isMaintenanceMode } = useMaintenanceMode();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<'google' | 'linkedin' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Handle Google Sign-In using OAuth 2.0 redirect flow (more reliable than FedCM/One Tap)
  const handleGoogleSignIn = async () => {
    trackSignupStart('google');
    setIsLoading(true);
    setLoadingProvider('google');
    setError(null);

    try {
      // Store state for CSRF protection
      const state = Math.random().toString(36).substring(7);
      localStorage.setItem('google_oauth_state', state);
      
      // Build Google OAuth 2.0 authorization URL (redirect flow)
      const redirectUri = `${window.location.origin}/auth/google/callback`;
      const scope = encodeURIComponent('openid email profile');
      
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${GOOGLE_CLIENT_ID}` +
        `&redirect_uri=${encodeURIComponent(redirectUri)}` +
        `&response_type=code` +
        `&scope=${scope}` +
        `&state=${state}` +
        `&access_type=offline` +
        `&prompt=consent`;
      
      // Redirect to Google authorization page
      window.location.href = googleAuthUrl;
    } catch (error: any) {
      console.error('Error starting Google Sign-In:', error);
      setError('Failed to start Google Sign-In. Please try again.');
      setIsLoading(false);
      setLoadingProvider(null);
    }
  };

  // Handle LinkedIn Sign-In
  const handleLinkedInSignIn = () => {
    if (!LINKEDIN_CLIENT_ID) {
      setError('LinkedIn Sign-In is not configured. Please contact support.');
      return;
    }

    trackSignupStart('linkedin');
    setIsLoading(true);
    setLoadingProvider('linkedin');
    setError(null);
    trackSignupStart('linkedin');

    // LinkedIn OAuth 2.0 authorization URL
    const scope = encodeURIComponent('openid profile email');
    const state = encodeURIComponent(Math.random().toString(36).substring(7));
    
    // Store state for CSRF protection (use localStorage for reliability across redirects)
    localStorage.setItem('linkedin_oauth_state', state);
    
    const linkedInAuthUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(LINKEDIN_REDIRECT_URI)}&state=${state}&scope=${scope}`;
    
    // Redirect to LinkedIn authorization page
    window.location.href = linkedInAuthUrl;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white dark:bg-[#0F172A] rounded-2xl shadow-2xl p-8 relative">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <X size={20} className="text-gray-500 dark:text-gray-400" />
          </button>

          {/* Branding Icon */}
          <div className="flex justify-center mb-6">
            <Logo size="lg" />
          </div>

          {/* Welcome Text */}
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 text-center">
            {isMaintenanceMode ? 'Under Maintenance' : 'Welcome to xRepl.AI'}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8 text-center text-sm">
            {isMaintenanceMode 
              ? 'Sign-in is temporarily unavailable. Please check back soon.'
              : 'Continue with your existing account'}
          </p>

          {isMaintenanceMode ? (
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
                <AlertTriangle size={32} className="text-yellow-600 dark:text-yellow-400" />
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                We're performing scheduled maintenance to improve your experience. 
                Please try again in a few minutes.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-all font-medium"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {/* OAuth Buttons */}
              <div className="flex flex-col gap-4">
                {/* Google Sign-In Button */}
                <button
                  onClick={handleGoogleSignIn}
                  disabled={isLoading}
                  className="w-full h-12 px-4 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingProvider === 'google' ? (
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  )}
                  <span className="text-gray-700 dark:text-gray-200 font-medium">
                    Continue with Google
                  </span>
                </button>

                {/* LinkedIn Sign-In Button */}
                <button
                  onClick={handleLinkedInSignIn}
                  disabled={isLoading}
                  className="w-full h-12 px-4 bg-[#0A66C2] hover:bg-[#004182] rounded-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loadingProvider === 'linkedin' ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="white">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  )}
                  <span className="text-white font-medium">
                    Continue with LinkedIn
                  </span>
                </button>

                {/* Error Message */}
                {error && (
                  <p className="text-sm text-red-600 dark:text-red-400 text-center mt-2">
                    {error}
                  </p>
                )}
              </div>

              {/* Terms */}
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-8 text-center">
                By continuing, you agree to xRepl.AI's{' '}
                <a href="/terms" className="underline hover:text-gray-700 dark:hover:text-gray-300">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/privacy" className="underline hover:text-gray-700 dark:hover:text-gray-300">
                  Privacy Policy
                </a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
